/**
 * Pure-function state machine for call reconnection / network-change handling.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallReconnectionStateMachine.swift`.
 *
 * ## Two entry points
 *
 * `transition(state, event)` — driven by external events (connected,
 * networkDrop, callEnded). Never produces `disconnectedAfterFailure`; that
 * terminal state is time-gated and can only be reached via `tick`.
 *
 * `tick(state, now)` — driven by the caller's periodic timer. Advances
 * time-based deadlines: unstable → reconnecting after 3 s, reconnecting →
 * disconnectedAfterFailure after 30 s. This is the ONLY path to the terminal
 * failure state, which enforces the critical invariant:
 *
 *   **A forged or spurious event cannot terminate the call UI.**
 *
 * Both functions are pure: no side effects, no global state, no DOM.
 * The caller owns the clock and the dispatch loop.
 *
 * ## State diagram
 *
 * ```
 *  connecting ──connected──▶ connected
 *                               │
 *                          networkDrop
 *                               │
 *                               ▼
 *                            unstable ──(tick, >3s)──▶ reconnecting
 *                                                           │  │
 *                                                  connected│  │tick, >30s
 *                                                           │  │
 *                                                           ▼  ▼
 *                                                        connected  disconnectedAfterFailure (terminal)
 *
 *  Any state ──callEnded──▶ ended (terminal)
 * ```
 */

import {
  type CallReconnectionState,
  type CallReconnectionEvent,
  type NetworkPath,
  makeConnected,
  makeUnstable,
  makeReconnecting,
  DISCONNECTED_AFTER_FAILURE,
  ENDED,
  UNSTABLE_TO_RECONNECTING_MS,
  RECONNECTING_TO_FAILED_MS,
} from "./CallReconnectionState";

// ---------------------------------------------------------------------------
// transition — event-driven transitions only
// ---------------------------------------------------------------------------

/**
 * Apply a single event to the current state and return the next state.
 *
 * ### Critical constraint
 * This function NEVER returns `disconnectedAfterFailure`. That terminal state
 * is reachable ONLY via `tick(state, now)` after the 30-second deadline
 * elapses. This guarantees that no single forged event (e.g. a brief ICE
 * failure re-fired by a misbehaving adapter) can push the UI into the "call
 * failed" terminal state.
 *
 * @param state - Current state (may be any `CallReconnectionState`).
 * @param event - The event to process.
 * @returns The new state after applying the event.
 */
export function transition(
  state: CallReconnectionState,
  event: CallReconnectionEvent,
): CallReconnectionState {
  // Terminal states absorb all events — the call is over.
  if (state.kind === "disconnectedAfterFailure" || state.kind === "ended") {
    return state;
  }

  switch (event.kind) {
    case "callEnded":
      // Any non-terminal state → ended on clean hangup.
      return ENDED;

    case "connected":
      // connecting or reconnecting → connected on successful (re)connection.
      // connected → connected is a no-op (idempotent).
      // unstable → connected: network recovered before deadline.
      if (
        state.kind === "connecting" ||
        state.kind === "reconnecting" ||
        state.kind === "unstable" ||
        state.kind === "connected"
      ) {
        return makeConnected();
      }
      return state;

    case "networkDrop": {
      // connected or unstable → unstable on a network drop.
      // connecting: a drop before the first connect is still unstable.
      // reconnecting: already in reconnect; a new drop keeps us there (tick
      //   owns the deadline, not the event).
      const path: NetworkPath = event.networkPath;
      if (
        state.kind === "connected" ||
        state.kind === "connecting" ||
        state.kind === "unstable"
      ) {
        // Reset the unstable timer on a fresh drop so the 3-second grace
        // period begins from this event, not from a stale prior drop.
        return makeUnstable(Date.now(), path);
      }
      // reconnecting + networkDrop: stay in reconnecting.
      return state;
    }
  }
}

// ---------------------------------------------------------------------------
// tick — time-based deadline transitions only
// ---------------------------------------------------------------------------

/**
 * Advance time-based deadlines. Must be called periodically by the owner
 * (e.g. every 500 ms or on `requestAnimationFrame`).
 *
 * ### Time-only transitions
 * - `unstable` + elapsed > `UNSTABLE_TO_RECONNECTING_MS` → `reconnecting`
 * - `reconnecting` + elapsed > `RECONNECTING_TO_FAILED_MS` → `disconnectedAfterFailure`
 *
 * `disconnectedAfterFailure` is exclusively produced here. No event pathway
 * reaches it — only the passage of time while in `reconnecting`.
 *
 * All other states are returned unchanged.
 *
 * @param state - Current state.
 * @param now   - Current timestamp in milliseconds (e.g. `Date.now()`).
 * @returns The new state after applying deadline checks.
 */
export function tick(
  state: CallReconnectionState,
  now: number,
): CallReconnectionState {
  switch (state.kind) {
    case "unstable": {
      const elapsed = now - state.enteredAt;
      if (elapsed > UNSTABLE_TO_RECONNECTING_MS) {
        return makeReconnecting(now);
      }
      return state;
    }

    case "reconnecting": {
      const elapsed = now - state.enteredAt;
      if (elapsed > RECONNECTING_TO_FAILED_MS) {
        // CRITICAL: this is the ONLY path to disconnectedAfterFailure.
        return DISCONNECTED_AFTER_FAILURE;
      }
      return state;
    }

    // All other states are time-inert.
    case "connecting":
    case "connected":
    case "disconnectedAfterFailure":
    case "ended":
      return state;
  }
}
